import React from 'react'
import { version } from '../../package.json'

const WHATS_NEW_KEY = 'vending_admin_last_seen_version'

const features = [
  {
    version: '2.3.0',
    items: [
      {
        icon: 'fas fa-clipboard-list',
        color: 'text-warning',
        title: 'Daily Replenishment Planning Sheet',
        description: 'Download a ready-to-use checklist every morning that shows exactly what needs to be refilled across all your machines, slot by slot, so your team can plan their rounds without guessing.'
      },
      {
        icon: 'fas fa-clock',
        color: 'text-info',
        title: 'See When Each Machine Was Last Active',
        description: 'The Devices page now shows when each machine last reported in, so you can instantly spot machines that haven\'t been heard from recently.'
      },
      {
        icon: 'fas fa-search',
        color: 'text-primary',
        title: 'Faster Device Assignment for Users',
        description: 'When setting up which machines a user can access, you can now search by name or ID instead of scrolling through the full list.'
      }
    ]
  },
  {
    version: '2.2.0',
    items: [
      {
        icon: 'fas fa-sort',
        color: 'text-primary',
        title: 'Sortable Columns & Excel Export',
        description: 'Click any column header on the device list to sort instantly. The Export button now downloads an Excel (.xlsx) file for easier filtering and analysis.'
      },
      {
        icon: 'fas fa-eye-slash',
        color: 'text-warning',
        title: 'Enable/Disable Products',
        description: 'Disable products in the master list to hide them from slot-assignment search in the web portal and replenish app, preventing field workers from selecting unavailable variants.'
      }
    ]
  },
  {
    version: '2.1.0',
    items: [
      {
        icon: 'fas fa-headset',
        color: 'text-primary',
        title: 'Customer Care Settings',
        description: 'Configure phone, email, WhatsApp chat, and custom website/feedback portal URL for each device.'
      },
      {
        icon: 'fas fa-chart-bar',
        color: 'text-success',
        title: 'Live Devices Chart',
        description: 'Dashboard now shows daily active device counts with 7D, 30D, and 90D period filters.'
      },
      {
        icon: 'fas fa-credit-card',
        color: 'text-info',
        title: 'Smart Card Reports',
        description: 'View and export Smart Card transactions with support for legacy and modern device formats.'
      },
      {
        icon: 'fas fa-qrcode',
        color: 'text-warning',
        title: 'QR Replenishment System',
        description: 'Generate QR codes for field workers to access mobile-optimized replenishment interfaces.'
      }
    ]
  }
]

export function shouldShowWhatsNew() {
  const lastSeenVersion = localStorage.getItem(WHATS_NEW_KEY)
  return lastSeenVersion !== version
}

export function markWhatsNewAsSeen() {
  localStorage.setItem(WHATS_NEW_KEY, version)
}

export function getNewFeaturesSince(lastVersion) {
  if (!lastVersion) return features
  return features.filter(f => f.version > lastVersion)
}

export default function WhatsNewModal({ show, onClose }) {
  if (!show) return null

  const handleClose = () => {
    markWhatsNewAsSeen()
    onClose()
  }

  const currentFeatures = features.find(f => f.version === version)?.items || []

  return (
    <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header bg-primary text-white">
            <h5 className="modal-title">
              <i className="fas fa-gift me-2"></i>
              What's New in v{version}
            </h5>
            <button 
              type="button" 
              className="btn-close btn-close-white" 
              onClick={handleClose}
            ></button>
          </div>
          <div className="modal-body">
            <p className="text-muted mb-4">
              We've added new features to help you manage your vending devices better!
            </p>
            
            <div className="list-group list-group-flush">
              {currentFeatures.map((feature, index) => (
                <div key={index} className="list-group-item border-0 px-0">
                  <div className="d-flex align-items-start">
                    <div className={`${feature.color} me-3`} style={{ fontSize: '1.5rem', width: '40px', textAlign: 'center' }}>
                      <i className={feature.icon}></i>
                    </div>
                    <div>
                      <h6 className="mb-1">{feature.title}</h6>
                      <small className="text-muted">{feature.description}</small>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="modal-footer">
            <button 
              type="button" 
              className="btn btn-primary w-100" 
              onClick={handleClose}
            >
              <i className="fas fa-check me-2"></i>
              Got it!
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
